import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  limit,
  Timestamp
} from 'firebase/firestore';
import { db } from './config';
import { Product, ProductFilter, ProductSort, generateUniqueSlug } from '../types/product';
import { Collection, HomepageSettings } from '../types/collection';

// Firestore service
export class FirestoreService {
  // Generic method to get all documents from a collection
  static async getCollection(collectionName: string) {
    try {
      const querySnapshot = await getDocs(collection(db, collectionName));
      const documents = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      return { success: true, data: documents };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  // Get a single document by ID
  static async getDocument(collectionName: string, docId: string) {
    try {
      const docRef = doc(db, collectionName, docId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { success: true, data: { id: docSnap.id, ...docSnap.data() } };
      } else {
        return { success: false, error: 'Document not found' };
      }
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  // Add a new document
  static async addDocument(collectionName: string, data: any) {
    try {
      const docRef = await addDoc(collection(db, collectionName), {
        ...data,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      return { success: true, id: docRef.id };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  // Update a document
  static async updateDocument(collectionName: string, docId: string, data: any) {
    try {
      const docRef = doc(db, collectionName, docId);
      await updateDoc(docRef, {
        ...data,
        updatedAt: new Date()
      });
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  // Delete a document
  static async deleteDocument(collectionName: string, docId: string) {
    try {
      const docRef = doc(db, collectionName, docId);
      await deleteDoc(docRef);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  // Query documents with conditions
  static async queryDocuments(
    collectionName: string, 
    conditions: Array<{ field: string; operator: any; value: any }>,
    orderByField?: string,
    orderDirection?: 'asc' | 'desc',
    limitCount?: number
  ) {
    try {
      let q = query(collection(db, collectionName));
      
      // Add where conditions
      conditions.forEach(condition => {
        q = query(q, where(condition.field, condition.operator, condition.value));
      });
      
      // Add ordering
      if (orderByField) {
        q = query(q, orderBy(orderByField, orderDirection || 'asc'));
      }
      
      // Add limit
      if (limitCount) {
        q = query(q, limit(limitCount));
      }
      
      const querySnapshot = await getDocs(q);
      const documents = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      return { success: true, data: documents };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }
}

// Specific collections
export class ProductsService {
  // Get all products
  static async getProducts() {
    try {
      console.log('🔍 Fetching all products from Firestore...');
      const querySnapshot = await getDocs(collection(db, 'products'));
      console.log('📦 Total products in Firestore:', querySnapshot.size);
      
      const products = querySnapshot.docs.map(doc => {
        const data = doc.data();
        console.log('📄 Product:', doc.id, data.name || 'No name', 'category:', data.category || 'No category', 'isActive:', data.isActive);
        return {
          id: doc.id,
          ...data,
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate()
        };
      }) as Product[];
      
      console.log('✅ Returning', products.length, 'products');
      return { success: true, data: products };
    } catch (error: any) {
      console.error('❌ Error fetching all products:', error);
      return { success: false, error: error.message };
    }
  }

  // Get product by ID
  static async getProduct(productId: string) {
    try {
      const docRef = doc(db, 'products', productId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return { 
          success: true, 
          data: { 
            id: docSnap.id, 
            ...data,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate()
          } as Product
        };
      } else {
        return { success: false, error: 'Product not found' };
      }
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  // Get product by slug
  static async getProductBySlug(slug: string) {
    try {
      const q = query(
        collection(db, 'products'),
        where('slug', '==', slug),
        where('isActive', '==', true)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return { success: false, error: 'Product not found' };
      }
      
      const doc = querySnapshot.docs[0];
      const data = doc.data();
      
      return { 
        success: true, 
        data: { 
          id: doc.id, 
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        } as Product
      };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  // Get product by name
  static async getProductByName(name: string) {
    try {
      const q = query(
        collection(db, 'products'),
        where('name', '==', name),
        where('isActive', '==', true)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return { success: false, error: 'Product not found' };
      }
      
      const doc = querySnapshot.docs[0];
      const data = doc.data();
      
      return { 
        success: true, 
        data: { 
          id: doc.id, 
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        } as Product
      };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  // Add new product
  static async addProduct(productData: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>) {
    try {
      const now = new Date();
      
      // Mevcut slug'ları kontrol et
      const existingProducts = await this.getProducts();
      const existingSlugs = existingProducts.success && existingProducts.data 
        ? existingProducts.data.map(p => p.slug) 
        : [];
      
      // Benzersiz slug oluştur
      let slug = productData.slug;
      if (!slug || existingSlugs.includes(slug)) {
        slug = generateUniqueSlug(productData.name, existingSlugs);
      }
      
      const docRef = await addDoc(collection(db, 'products'), {
        ...productData,
        slug,
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now)
      });
      return { success: true, id: docRef.id, slug };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  // Update product
  static async updateProduct(productId: string, productData: Partial<Product>) {
    try {
      const docRef = doc(db, 'products', productId);
      await updateDoc(docRef, {
        ...productData,
        updatedAt: Timestamp.fromDate(new Date())
      });
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  // Delete product
  static async deleteProduct(productId: string) {
    try {
      const docRef = doc(db, 'products', productId);
      await deleteDoc(docRef);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  // Get products by category
  static async getProductsByCategory(category: string) {
    try {
      console.log('🔍 Fetching products for category:', category);
      
      // orderBy olmadan deneyelim
      const q = query(
        collection(db, 'products'),
        where('category', '==', category),
        where('isActive', '==', true)
      );
      
      const querySnapshot = await getDocs(q);
      console.log('📦 Query result:', querySnapshot.size, 'products found');
      
      const products = querySnapshot.docs.map(doc => {
        const data = doc.data();
        console.log('📄 Product:', doc.id, data.name, 'category:', data.category, 'isActive:', data.isActive);
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        };
      }) as Product[];
      
      // Client-side sorting
      products.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return b.createdAt.getTime() - a.createdAt.getTime();
        }
        return 0;
      });
      
      console.log('✅ Returning', products.length, 'products');
      return { success: true, data: products };
    } catch (error: any) {
      console.error('❌ Error fetching products by category:', error);
      // Hata durumunda tüm ürünleri getirip client-side filtreleme yapalım
      try {
        console.log('🔄 Trying fallback: get all products and filter');
        const allProductsResult = await this.getProducts();
        if (allProductsResult.success && allProductsResult.data) {
          console.log('📦 All products:', allProductsResult.data.length);
          const filteredProducts = allProductsResult.data.filter(product => {
            const matches = product.category === category && product.isActive === true;
            if (!matches) {
              console.log('🚫 Filtered out:', product.name, 'category:', product.category, 'isActive:', product.isActive);
            }
            return matches;
          });
          console.log('✅ Fallback returning', filteredProducts.length, 'filtered products');
          return { success: true, data: filteredProducts };
        }
      } catch (fallbackError) {
        console.error('❌ Fallback hatası:', fallbackError);
      }
      
      return { success: false, error: error.message };
    }
  }

  // Get products by subcategory
  static async getProductsBySubcategory(category: string, subcategory: string) {
    try {
      const q = query(
        collection(db, 'products'),
        where('category', '==', category),
        where('subcategory', '==', subcategory),
        where('isActive', '==', true),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const products = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      })) as Product[];
      
      return { success: true, data: products };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  // Get featured products
  static async getFeaturedProducts() {
    try {
      const q = query(
        collection(db, 'products'),
        where('isFeatured', '==', true),
        where('isActive', '==', true),
        orderBy('createdAt', 'desc'),
        limit(8)
      );
      
      const querySnapshot = await getDocs(q);
      const products = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      })) as Product[];
      
      return { success: true, data: products };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  // Get products on sale
  static async getProductsOnSale() {
    try {
      const q = query(
        collection(db, 'products'),
        where('isOnSale', '==', true),
        where('isActive', '==', true),
        orderBy('discount', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const products = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      })) as Product[];
      
      return { success: true, data: products };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  // Search products
  static async searchProducts(searchTerm: string) {
    try {
      const q = query(
        collection(db, 'products'),
        where('isActive', '==', true),
        orderBy('name')
      );
      
      const querySnapshot = await getDocs(q);
      const allProducts = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      })) as Product[];
      
      // Client-side filtering for search
      const filteredProducts = allProducts.filter(product => 
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.shortDescription.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      
      return { success: true, data: filteredProducts };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  // Filter products
  static async filterProducts(filters: ProductFilter, sort?: ProductSort) {
    try {
      let q = query(
        collection(db, 'products'),
        where('isActive', '==', true)
      );

      // Apply filters
      if (filters.category) {
        q = query(q, where('category', '==', filters.category));
      }
      
      if (filters.subcategory) {
        q = query(q, where('subcategory', '==', filters.subcategory));
      }
      
      if (filters.isOnSale !== undefined) {
        q = query(q, where('isOnSale', '==', filters.isOnSale));
      }
      
      if (filters.isFeatured !== undefined) {
        q = query(q, where('isFeatured', '==', filters.isFeatured));
      }

      // Apply sorting
      if (sort) {
        q = query(q, orderBy(sort.field, sort.direction));
      } else {
        q = query(q, orderBy('createdAt', 'desc'));
      }

      const querySnapshot = await getDocs(q);
      let products = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      })) as Product[];

      // Client-side filtering for complex filters
      if (filters.minPrice !== undefined) {
        products = products.filter(p => p.price >= filters.minPrice!);
      }
      
      if (filters.maxPrice !== undefined) {
        products = products.filter(p => p.price <= filters.maxPrice!);
      }
      
      if (filters.colors && filters.colors.length > 0) {
        products = products.filter(p => 
          p.colors.some(color => filters.colors!.includes(color))
        );
      }
      
      if (filters.sizes && filters.sizes.length > 0) {
        products = products.filter(p => 
          p.sizes.some(size => filters.sizes!.includes(size.size))
        );
      }
      
      if (filters.materials && filters.materials.length > 0) {
        products = products.filter(p => 
          p.materials.some(material => filters.materials!.includes(material))
        );
      }

      return { success: true, data: products };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }
}

export class UsersService {
  static async getUsers() {
    return FirestoreService.getCollection('users');
  }

  static async getUser(userId: string) {
    return FirestoreService.getDocument('users', userId);
  }

  static async addUser(userData: any) {
    return FirestoreService.addDocument('users', userData);
  }

  static async updateUser(userId: string, userData: any) {
    return FirestoreService.updateDocument('users', userId, userData);
  }
}

export class CollectionsService {
  // Get all collections
  static async getCollections() {
    try {
      const q = query(
        collection(db, 'collections'),
        orderBy('order', 'asc')
      );
      const querySnapshot = await getDocs(q);
      const collections = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      })) as Collection[];
      return { success: true, data: collections };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  // Get active collections
  static async getActiveCollections() {
    try {
      const q = query(
        collection(db, 'collections'),
        where('isActive', '==', true),
        orderBy('order', 'asc')
      );
      const querySnapshot = await getDocs(q);
      const collections = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      })) as Collection[];
      return { success: true, data: collections };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  // Get collection by ID
  static async getCollection(collectionId: string) {
    try {
      const docRef = doc(db, 'collections', collectionId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return { 
          success: true, 
          data: { 
            id: docSnap.id, 
            ...data,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate()
          } as Collection
        };
      } else {
        return { success: false, error: 'Collection not found' };
      }
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  // Add new collection
  static async addCollection(collectionData: Omit<Collection, 'id' | 'createdAt' | 'updatedAt'>) {
    try {
      const now = new Date();
      const docRef = await addDoc(collection(db, 'collections'), {
        ...collectionData,
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now)
      });
      return { success: true, id: docRef.id };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  // Update collection
  static async updateCollection(collectionId: string, collectionData: Partial<Collection>) {
    try {
      const docRef = doc(db, 'collections', collectionId);
      await updateDoc(docRef, {
        ...collectionData,
        updatedAt: Timestamp.fromDate(new Date())
      });
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  // Delete collection
  static async deleteCollection(collectionId: string) {
    try {
      const docRef = doc(db, 'collections', collectionId);
      await deleteDoc(docRef);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  // Add product to collection
  static async addProductToCollection(collectionId: string, productId: string) {
    try {
      const collectionResult = await this.getCollection(collectionId);
      if (!collectionResult.success || !collectionResult.data) {
        return { success: false, error: 'Collection not found' };
      }
      
      const currentProducts = collectionResult.data.products || [];
      if (currentProducts.includes(productId)) {
        return { success: false, error: 'Product already in collection' };
      }
      
      const docRef = doc(db, 'collections', collectionId);
      await updateDoc(docRef, {
        products: [...currentProducts, productId],
        updatedAt: Timestamp.fromDate(new Date())
      });
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  // Remove product from collection
  static async removeProductFromCollection(collectionId: string, productId: string) {
    try {
      const collectionResult = await this.getCollection(collectionId);
      if (!collectionResult.success || !collectionResult.data) {
        return { success: false, error: 'Collection not found' };
      }
      
      const currentProducts = collectionResult.data.products || [];
      const updatedProducts = currentProducts.filter((id: string) => id !== productId);
      
      const docRef = doc(db, 'collections', collectionId);
      await updateDoc(docRef, {
        products: updatedProducts,
        updatedAt: Timestamp.fromDate(new Date())
      });
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }
}

export class HomepageSettingsService {
  // Get homepage settings
  static async getHomepageSettings() {
    try {
      const q = query(
        collection(db, 'homepageSettings'),
        limit(1)
      );
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const data = doc.data();
        return {
          success: true,
          data: {
            id: doc.id,
            ...data,
            updatedAt: data.updatedAt?.toDate()
          } as HomepageSettings
        };
      } else {
        // Default settings
        const defaultSettings: Omit<HomepageSettings, 'id'> = {
          heroSlides: [],
          collections: [],
          updatedAt: new Date()
        };
        return { success: true, data: defaultSettings as HomepageSettings };
      }
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  // Update homepage settings
  static async updateHomepageSettings(settings: Partial<HomepageSettings>) {
    try {
      const currentSettings = await this.getHomepageSettings();
      
      if (currentSettings.success && currentSettings.data?.id) {
        // Update existing settings
        const docRef = doc(db, 'homepageSettings', currentSettings.data.id);
        await updateDoc(docRef, {
          ...settings,
          updatedAt: Timestamp.fromDate(new Date())
        });
        return { success: true };
      } else {
        // Create new settings
        const now = new Date();
        const defaultSettings = {
          heroSlides: [],
          collections: [],
          ...settings,
          updatedAt: now
        };
        const docRef = await addDoc(collection(db, 'homepageSettings'), {
          ...defaultSettings,
          updatedAt: Timestamp.fromDate(now)
        });
        return { success: true, id: docRef.id };
      }
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  // Update hero slides
  static async updateHeroSlides(slides: string[]) {
    return this.updateHomepageSettings({ heroSlides: slides });
  }

  // Update collections order
  static async updateCollectionsOrder(collectionIds: string[]) {
    return this.updateHomepageSettings({ collections: collectionIds });
  }

  // Initialize default settings with Google Drive images
  static async initializeDefaultSettings() {
    try {
      const currentSettings = await this.getHomepageSettings();
      
      // Eğer ayarlar zaten varsa, initialize etme
      if (currentSettings.success && currentSettings.data?.id) {
        return { success: true, message: 'Settings already exist' };
      }

      // Google Drive klasöründen görüntü linkleri
      // NOT: Google Drive klasör linki direkt görüntü olarak kullanılamaz
      // Her dosya için paylaşılabilir görüntü linki gerekir
      // Format: https://drive.google.com/uc?export=view&id=FILE_ID
      // Veya: https://lh3.googleusercontent.com/d/FILE_ID
      
      const defaultHeroSlides = [
        // Google Drive klasörü: https://drive.google.com/drive/folders/1TcvOnRa8wF7i4oHkLVKg4DkqB0hLu9gc
        // Klasör içindeki dosyaların görüntü linklerini buraya ekleyebilirsiniz
        // Örnek format (dosya ID'si ile):
        'https://drive.google.com/uc?export=view&id=FILE_ID_HERE'
      ];

      const now = new Date();
      const defaultSettings = {
        heroSlides: defaultHeroSlides,
        collections: [],
        updatedAt: now
      };

      const docRef = await addDoc(collection(db, 'homepageSettings'), {
        ...defaultSettings,
        updatedAt: Timestamp.fromDate(now)
      });

      return { success: true, id: docRef.id };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }
}

export default FirestoreService;
